import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, ArrowUpRight } from 'lucide-react';
import type { WidgetProps } from '../../../types/dashboard';
import { supabase } from '../../../lib/supabase';
import { format, isToday } from 'date-fns';

interface CalendarEvent {
  id: string;
  title: string;
  start_date: string;
  end_date?: string;
  color?: string;
  tags: string[];
}

export const CalendarWidget: React.FC<WidgetProps> = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = new Date();
  const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
  const monthDay = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  useEffect(() => {
    loadTodaysEvents();
  }, []);

  const loadTodaysEvents = async () => {
    try {
      setLoading(true);
      
      // Get today's start and end
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      const { data, error: fetchError } = await supabase
        .from('calendar_events')
        .select('*')
        .gte('start_date', todayStart.toISOString())
        .lte('start_date', todayEnd.toISOString())
        .order('start_date', { ascending: true });

      if (fetchError) throw fetchError;
      setEvents(data || []);

    } catch (err) {
      console.error('Error loading events:', err);
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const getEventColor = (tags: string[]) => {
    if (tags.includes('meeting')) return 'bg-blue-500 dark:bg-blue-600';
    if (tags.includes('content')) return 'bg-yellow-500 dark:bg-yellow-600';
    if (tags.includes('schedule')) return 'bg-green-500 dark:bg-green-600';
    return 'bg-indigo-500 dark:bg-indigo-600';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
            <CalendarIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">Today's Schedule</div>
        </div>
        <Link to="/calendar" className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center hover:underline">
          Calendar <ArrowUpRight className="ml-1 h-3 w-3" />
        </Link>
      </div>

      <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-indigo-900 dark:text-indigo-100">{dayName}</div>
            <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mt-1">{monthDay}</div>
          </div>
          <div className="flex items-center text-indigo-600 dark:text-indigo-400">
            <Clock className="h-4 w-4 mr-1.5" />
            <span className="text-sm">{today.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto space-y-3 no-scrollbar">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <CalendarIcon className="h-8 w-8 text-gray-400 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No events scheduled for today</p>
            <Link 
              to="/calendar" 
              className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Schedule something
            </Link>
          </div>
        ) : (
          events.map((event) => (
            <div
              key={event.id}
              className="flex items-center p-4 bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl hover:shadow-sm transition-all duration-200"
            >
              <div className="flex items-center space-x-4 flex-1">
                <div className={`w-1.5 h-12 rounded-full ${event.color || getEventColor(event.tags)}`}></div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">{event.title}</h4>
                  <div className="flex items-center mt-1.5">
                    <Clock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 mr-1.5" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {format(new Date(event.start_date), 'h:mm a')}
                    </p>
                  </div>
                </div>
              </div>
              {event.tags.length > 0 && (
                <div className="px-2.5 py-1 bg-gray-100 dark:bg-dark-700 rounded-md text-xs text-gray-600 dark:text-gray-300 capitalize">
                  {event.tags[0]}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};